import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import gamTimeouts from '../../consts/config/gamTimeouts';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import * as muleSoftMethods from '../../consts/muleSoft/muleSoftMethods';
import helpers from '../../utils/helpers';
import urlUtils from '../../utils/urlUtils';
import gigyaMockService from '../auth/gigyaMockService';
import cookieService from '../cookie/cookieService';
import HttpErrorHandlerService from '../httpErrorHandlerService';
import log from '../logger/log';
import marketTypeService from '../marketTypeService';
import appRouterService from '../route/appRouterService';
import server from '../server/server';
import storageService from '../storage/storageService';

let gigyaJWT;
let gigyaUid;
let gigyaLT;
let isLTRefresh = false;

const getGigyaJWTFromState = () => gigyaJWT;
const setGigyaJWTToState = (value) => (gigyaJWT = value);

const getGigyaUidFromState = () => gigyaUid;
const setGigyaUidToState = (value) => (gigyaUid = value);

const getGigyaLTFromState = () => gigyaLT;
const setGigyaLTToState = (value) => (gigyaLT = value);

const getIsLTRefresh = () => isLTRefresh;
const setIsLTRefresh = (value) => (isLTRefresh = value);

const getGigyaRefreshToken = (isRefreshCall) => {
    try {
        const gigyaRefreshUrl = appConfig.getGigyaRefreshUrl();
        const gigyaApiKey = appConfig.getGigyaApiKey();

        if (!gigyaRefreshUrl || !gigyaApiKey) return;

        const gigyaLTFromCookies = cookieService.getGigyaLTFromCookies();

        if (gigyaLTFromCookies) {
            setIsLTRefresh(true);
            setGigyaLTToState(gigyaLTFromCookies);

            return gigyaLTFromCookies;
        }

        const gigyaLTFromState = getGigyaLTFromState();

        if (gigyaLTFromState) {
            if (isRefreshCall) {
                setGigyaLTToState();
            }

            return gigyaLTFromState;
        }
    } catch (e) {
        log.info(`gamGigyaService: getGigyaRefreshToken parse error: ${e}`);
    }
    return null;
};

export const getGigyaTokenAsync = async (throwIfNull) => {
    const isDCE2 = marketTypeService.isDCE2();

    //gigya exists for dce2 only
    if (!isDCE2) return;

    const isTestLoginEnabled = appConfig.getIsTestLoginEnabled();

    if (isTestLoginEnabled) {
        await gigyaMockService.setMockGigyaToken();
    }

    return getGigyaToken(throwIfNull);
};

export const getGigyaToken = (throwIfNull) => {
    try {
        const isDCE2 = marketTypeService.isDCE2();

        //gigya exists for dce2 only
        if (!isDCE2) return;

        // Get gigya jwt from state
        const gigyaJWTFromState = getGigyaJWTFromState();

        if (gigyaJWTFromState) return gigyaJWTFromState;

        //save LT token
        getGigyaRefreshToken();

        //save uid
        getGigyaUid();

        // Get gigya jwt from cookies
        const gigyaJWTFromCookies = cookieService.getGigyaJWTFromCookies();

        if (gigyaJWTFromCookies) {
            // Set gigiya jwt to js state, so next time it will be read from there and not from cookies
            setGigyaJWTToState(gigyaJWTFromCookies);

            return gigyaJWTFromCookies;
        }

        // Get gigya jwt from local storage
        // We leave it for backward compatibility
        const gigyaJWTFromStorage = storageService.getGamGigyaJWTFromStorage();

        if (gigyaJWTFromStorage) return gigyaJWTFromStorage;
    } catch (e) {
        log.info(`gamGigyaService: GigyaToken parse error: ${e}`);
    }

    if (throwIfNull) throw new Error('GigyaToken does not exist');

    return null;
};

export const getGigyaUid = () => {
    try {
        // Get gigya uid from state
        const gigyaUidFromState = getGigyaUidFromState();

        if (gigyaUidFromState) return gigyaUidFromState;

        // Get gigya uid from cookies
        const gigyaUidFromCookies = cookieService.getGigyaUidFromCookies();

        if (gigyaUidFromCookies) {
            // Set gigiya uid to js state, so next time it will be read from there and not from cookies
            setGigyaUidToState(gigyaUidFromCookies);

            return gigyaUidFromCookies;
        }

        // Get gigya uid from local storage
        // We leave it for backward compatibility
        const userData = storageService.getGamGigyaAccountDataFromStorage();
        const consumerId = userData?.myAccount?.uid;

        if (consumerId) return consumerId;
    } catch (e) {
        log.info(`gamGigyaService: getGigyaUid parse error: ${e}`);
    }
    return null;
};

export const refreshGigyaToken = async () => {
    log.info(`gamGigyaService: refreshGigyaToken invoked`);

    let isGigyaTokenRefreshed = null;
    const isTestLoginEnabled = appConfig.getIsTestLoginEnabled();

    try {
        if (isTestLoginEnabled) {
            await gigyaMockService.setMockGigyaToken();
            return true;
        }

        const gigyaLTToken = getGigyaRefreshToken();

        if (gigyaLTToken) {
            const response = await refreshGigyaTokenApi();
            const {statusCode, statusReason, id_token} = response || {};

            if (statusCode === 200 && id_token) {
                setGigyaJWTToState(id_token);
                return true;
            } else {
                log.error(
                    `gamGigyaService: refreshGigyaToken failed with status code: ${statusCode} and reason: ${statusReason}`,
                    logMessageTypes.GAM
                );
                appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
                return false;
            }
        }

        const isLTRefresh = getIsLTRefresh();

        if (isLTRefresh) {
            log.info(`gamGigyaService: refreshGigyaToken no LT token in state`, logMessageTypes.GAM);
            appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
            return false;
        }

        // eslint-disable-next-line no-undef
        if (typeof gigya === 'undefined' || gigya.accounts === 'undefined' || gigya.accounts.getJWT === 'undefined') {
            log.error(
                `gamGigyaService: refreshGigyaToken failed. gigya.accounts.getJWT not defined`,
                logMessageTypes.GAM
            );
            appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
            return false;
        }

        // eslint-disable-next-line no-undef
        gigya.accounts.getJWT({
            callback: async (accountInfo) => {
                if (accountInfo?.id_token && accountInfo.status === 'OK') {
                    setGigyaJWTToState(accountInfo.id_token);

                    isGigyaTokenRefreshed = true;
                } else {
                    isGigyaTokenRefreshed = false;
                    log.info(
                        `gamGigyaService: refreshGigyaToken failed with errorCode: ${accountInfo?.errorCode}. Bad accountInfo from gigya.accounts.getJWT`
                    );
                    appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
                }
            },
        });
    } catch (e) {
        isGigyaTokenRefreshed = false;
        log.error(`gamGigyaService: refreshGigyaToken failed. error: ${e}`, {logMessageType: logMessageTypes.GAM});
    }

    let maxWait = gamTimeouts.GAM_GIGYA_REFRESH_TOKEN_TIMEOUT;

    while (isGigyaTokenRefreshed === null) {
        maxWait--;
        if (maxWait <= 0) {
            isGigyaTokenRefreshed = false;
            log.error(
                `gamGigyaService: refreshGigyaToken failed. gigya getJWT callback have not been called after 10 sec`,
                logMessageTypes.GAM
            );
            appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
        }

        await helpers.timeout(1000);
    }

    return isGigyaTokenRefreshed;
};

export const refreshGigyaTokenApi = async () => {
    try {
        const methodUrl = urlUtils.join(appConfig.getGigyaRefreshUrl(), muleSoftMethods.GET_JWT);
        const apiKey = appConfig.getGigyaApiKey();
        const gigyaLTToken = getGigyaRefreshToken(true);
        const config = {
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
            data: {login_token: gigyaLTToken, apiKey, expiration: 3600},
        };

        const response = await server.post(methodUrl, config);

        return response?.data;
    } catch (e) {
        new HttpErrorHandlerService().handleError(e);
        throw e;
    }
};
